import React, { useState } from "react";

const stages = [
  { id: "stage-1", number: 1, title: "Retirement Planning with Constant Income" },
  { id: "stage-2", number: 2, title: "Incorporating Income Growth" },
  { id: "stage-3", number: 3, title: "Capital Preservation and Perpetual Income" },
  { id: "stage-4", number: 4, title: "Incorporating Existing Savings" },
  { id: "stage-5", number: 5, title: "Incorporating Inflation" },
];

function StageHeader({ number, title }) {
  return (
    <header className="stage-header">
      <span>Stage {number}</span>
      <h2>{title}</h2>
    </header>
  );
}

function BottomLine({ children }) {
  return (
    <div className="bottom-line">
      <span>Bottom line</span>
      <p>{children}</p>
    </div>
  );
}

function ChangesTable({ rows }) {
  return (
    <div className="example-table-wrap">
      <table className="example-table">
        <caption>Changes from the previous scenario</caption>
        <thead>
          <tr><th>Parameter</th><th>Previous value</th><th>New value</th></tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]}><th>{row[0]}</th><td>{row[1]}</td><td>{row[2]}</td></tr>
          ))}
        </tbody>
      </table>
      <p className="table-note">All other assumptions remain unchanged.</p>
    </div>
  );
}

function RecentGraduateExample({ expanded, onToggle }) {
  const [activeStage, setActiveStage] = useState(1);

  const selectStage = (stageNumber) => {
    setActiveStage(stageNumber);
    const card = document.querySelector(".career-example");
    if (card) card.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <article
      className={`career-example ${expanded ? "expanded" : "collapsed"}`}
      data-active-stage={activeStage}
    >
      <button
        type="button"
        className="career-example-toggle"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span>
          <small>Career Stage</small>
          <strong>Recent Graduate</strong>
        </span>
        <span className="collapse-action">
          {expanded ? "Hide example" : "Show example"}
          <i aria-hidden="true">{expanded ? "−" : "+"}</i>
        </span>
      </button>

      {expanded && <div className="career-example-body">
      <div className="career-example-intro">
        <span className="career-badge">Early career</span>
        <h1>Recent Graduate</h1>
        <p>
          Follow a retirement plan as it evolves from a conservative starting
          point into a more realistic long-term strategy.
        </p>
      </div>

      <nav className="stage-index" aria-label="Recent Graduate stages">
        {stages.map((stage) => (
          <button
            key={stage.id}
            type="button"
            className={`stage-tab stage-tab-${stage.number} ${activeStage === stage.number ? "active" : ""}`}
            onClick={() => setActiveStage(stage.number)}
            aria-selected={activeStage === stage.number}
            role="tab"
          >
            <span>{stage.number}</span>{stage.title}
          </button>
        ))}
      </nav>

      <section id="stage-1" className={`example-stage ${activeStage === 1 ? "" : "stage-hidden"}`} role="tabpanel">
        <StageHeader number="1" title="Retirement Planning with Constant Income" />
        <p>
          Although your current salary is <strong>$70,000</strong>, you decide
          to use a more conservative planning income of <strong>$60,000</strong>
          in today's dollars. Rather than planning to replace your entire income
          during retirement, you assume that 90% of your current salary
          (<strong>$63,000 per year</strong>) will be sufficient to maintain your
          desired lifestyle. These assumptions provide a realistic yet prudent
          starting point.
        </p>

        <div className="example-table-wrap">
          <table className="example-table assumptions-table">
            <caption>Planning assumptions</caption>
            <tbody>
              <tr><th>Current Retirement Savings</th><td>$0</td></tr>
              <tr><th>Current Annual Income</th><td>$70,000</td></tr>
              <tr><th>Replacement Rate</th><td>90%</td></tr>
              <tr><th>Desired Retirement Income</th><td>$63,000</td></tr>
              <tr><th>Years Until Retirement</th><td>40</td></tr>
              <tr><th>Years in Retirement</th><td>30</td></tr>
              <tr><th>Real Investment Return</th><td>4%</td></tr>
              <tr><th>Real Income Growth</th><td>0%</td></tr>
              <tr><th>Inflation Rate</th><td>0%</td></tr>
            </tbody>
          </table>
        </div>

        <div className="example-question">
          <span>Question</span>
          <p>How much should you save each year to achieve this goal?</p>
        </div>
        <BottomLine>
          You would need to save approximately <strong>$11,646 per year</strong>,
          or about <strong>14.56% of your income</strong>, to accumulate a
          retirement fund of approximately <strong>$1,106,690</strong>.
        </BottomLine>

        <h3>Understanding the results</h3>
        <p>
          This result is based on conservative assumptions. It assumes that
          income remains constant in real terms throughout your career and that
          you receive no income from Social Security, pensions, or other
          retirement plans. The required saving rate may therefore be higher
          than under more realistic assumptions. With inflation at zero, all
          amounts can be read directly in today's dollars.
        </p>
        <div className="looking-ahead">
          <h3>Looking ahead</h3>
          <p>Next, we incorporate income growth and examine whether a more realistic income path lowers the amount you need to save today.</p>
        </div>
      </section>

      <section id="stage-2" className={`example-stage ${activeStage === 2 ? "" : "stage-hidden"}`} role="tabpanel">
        <StageHeader number="2" title="Incorporating Income Growth" />
        <p>
          As you gain experience, your income may increase through promotions,
          job changes, and new skills. The model uses a <strong>real income
          growth rate</strong>: growth in purchasing power after removing
          inflation. Many calculators assume 0%; planners often use about 1%,
          while 2% may suit someone with strong advancement opportunities. As a
          recent graduate, we will assume <strong>2% annual real income growth</strong>.
        </p>
        <ChangesTable rows={[["Real Income Growth", "0.0%", "2.0%"]]} />
        <BottomLine>
          You would need to save approximately <strong>$8,536 per year</strong>,
          or about <strong>12.19% of current income</strong>, to accumulate the
          same retirement fund of approximately <strong>$1,106,690</strong>.
        </BottomLine>
        <h3>Understanding the results</h3>
        <p>
          The required retirement fund does not change because the objective is
          the same. What changes is the path to the goal. When income grows, a
          larger portion of lifetime savings can occur during higher-earning
          years, lowering the amount that must be saved today.
        </p>
        <div className="looking-ahead">
          <h3>Looking ahead</h3>
          <p>Next, we consider preserving the retirement fund indefinitely instead of gradually spending it down.</p>
        </div>
      </section>

      <section id="stage-3" className={`example-stage ${activeStage === 3 ? "" : "stage-hidden"}`} role="tabpanel">
        <StageHeader number="3" title="Capital Preservation and Perpetual Income" />
        <p>
          The previous stages allow the fund to be spent down over a fixed
          retirement period. That creates longevity risk: living longer than
          expected could deplete the savings. A capital-preservation strategy
          instead limits withdrawals to the annual real return, keeping the
          principal intact and allowing income to continue indefinitely.
        </p>
        <div className="formula-card">
          <span>Required Fund</span>
          <strong>Annual Retirement Income ÷ Real Rate of Return</strong>
        </div>
        <ChangesTable rows={[["Retirement Fund Objective", "Fund exhausted during retirement", "Fund preserved indefinitely"]]} />
        <BottomLine>
          To generate <strong>$63,000 per year indefinitely</strong>, you would
          need approximately <strong>$1,575,000</strong>. Reaching this target
          requires saving approximately <strong>$8,536 per year</strong>, or
          about <strong>12.19% of current income</strong>.
        </BottomLine>
        <h3>Understanding the results</h3>
        <p>
          The required fund is larger because the principal must remain intact;
          only its annual investment return can fund retirement income. This
          reduces the risk of outliving your assets and may leave principal for
          heirs, charitable giving, or unexpected expenses. Continued 2% real
          income growth helps keep the annual savings target manageable.
        </p>
        <div className="looking-ahead">
          <h3>Looking ahead</h3>
          <p>Next, we examine how retirement savings already accumulated can reduce future contributions.</p>
        </div>
      </section>

      <section id="stage-4" className={`example-stage ${activeStage === 4 ? "" : "stage-hidden"}`} role="tabpanel">
        <StageHeader number="4" title="Incorporating Existing Savings" />
        <p>
          Many people already have retirement assets through employer plans,
          individual retirement accounts, or personal investments. Suppose you
          have <strong>$50,000</strong> invested. Because those funds are already
          working toward the goal, the amount you must save in future years falls.
        </p>
        <ChangesTable rows={[["Current Retirement Savings", "$0", "$50,000"]]} />
        <BottomLine>
          With $50,000 already invested, you would need to save approximately
          <strong> $6,685 per year</strong>, or about <strong>9.55% of current
          income</strong>, to accumulate the required retirement fund.
        </BottomLine>
        <h3>Understanding the results</h3>
        <p>
          The retirement objective and required fund remain the same. Your
          starting point has changed: part of the goal is already prefunded, and
          the existing savings continue growing. Both the annual contribution
          and the percentage of income that must be saved are therefore lower.
        </p>
        <div className="looking-ahead">
          <h3>Looking ahead</h3>
          <p>Finally, we add inflation and express future income, retirement income, and fund values in nominal dollars.</p>
        </div>
      </section>

      <section id="stage-5" className={`example-stage ${activeStage === 5 ? "" : "stage-hidden"}`} role="tabpanel">
        <StageHeader number="5" title="Incorporating Inflation" />
        <p>
          So far, calculations have used today's dollars and 0% inflation. In
          reality, prices, wages, and retirement expenses rise over time. We now
          assume inflation averages <strong>3% per year</strong> during both the
          working years and retirement.
        </p>
        <ChangesTable rows={[["Inflation Rate", "0.0%", "3.0%"]]} />
        <h3>How the model incorporates inflation</h3>
        <p>
          Future income, retirement income, and fund values are shown in nominal
          dollars. You do not need to enter a nominal investment return: the
          model converts the real return to its corresponding nominal rate so
          the purchasing-power assumptions remain consistent.
        </p>
        <BottomLine>
          The first year's required savings remains <strong>$6,685</strong>.
          Future contributions then rise with inflation. Although dollar amounts
          change each year, the required savings rate remains <strong>8.36% of
          income</strong> under this scenario.
        </BottomLine>
        <h3>Understanding the results</h3>
        <p>
          Inflation makes future nominal values appear much larger, but it
          affects both income and savings. With a constant real return and
          consistently applied inflation, the required savings rate is driven by
          purchasing power rather than nominal dollar amounts.
        </p>

        <h3>Conclusion</h3>
        <p>
          The examples in this tutorial have progressively incorporated additional
          elements of realism: income growth, alternative retirement objectives,
          existing savings, and inflation.
        </p>
        <p>
          As your circumstances change over time, you can revisit the model and
          update its assumptions to reflect your current income, accumulated
          savings, expected investment returns, retirement goals, and economic
          outlook. The resulting plan can then evolve along with your financial life.
        </p>
      </section>

      <nav className="stage-pagination" aria-label="Navigate between stages">
        <button
          type="button"
          onClick={() => selectStage(activeStage - 1)}
          disabled={activeStage === 1}
        >
          <span aria-hidden="true">←</span> Previous stage
        </button>
        <span>Stage {activeStage} of {stages.length}</span>
        <button
          type="button"
          onClick={() => selectStage(activeStage + 1)}
          disabled={activeStage === stages.length}
        >
          Next stage <span aria-hidden="true">→</span>
        </button>
      </nav>

      </div>}
    </article>
  );
}

export default RecentGraduateExample;
